<template>
    <Page>
        <Container class="max-w-screen-xl">
            <div class="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <div class="text-xs font-bold uppercase text-brand-primary-500">V3ME UI reference</div>
                    <Title class="!mb-2">Brand Board</Title>
                    <Help class="!my-0 max-w-2xl">
                        Real V3ME components rendered inside the app shell. Use this before UI exploration so prototypes inherit existing controls, spacing, color, and density.
                    </Help>
                </div>
                <Button primary outlined size="sm" @click="$router.push('/workout')">
                    Open Workouts
                </Button>
            </div>

            <div class="grid gap-4 xl:grid-cols-12">
                <section class="rounded border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 xl:col-span-5">
                    <div class="border-b border-neutral-200 p-4 dark:border-neutral-800">
                        <Subtitle class="!mb-1">Color Tokens</Subtitle>
                        <Help class="!my-0">Brand primary is for action and state, not page decoration.</Help>
                    </div>
                    <div class="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
                        <div
                            v-for="color in colors"
                            :key="color.name"
                            class="min-h-24 rounded border border-white/10 p-3"
                            :class="color.textClass"
                            :style="{backgroundColor: color.hex}"
                        >
                            <div class="mt-10 text-xs font-bold">{{ color.name }}</div>
                            <div class="text-xs opacity-70">{{ color.hex }}</div>
                        </div>
                    </div>
                </section>

                <section class="rounded border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 xl:col-span-7">
                    <div class="border-b border-neutral-200 p-4 dark:border-neutral-800">
                        <Subtitle class="!mb-1">Controls</Subtitle>
                        <Help class="!my-0">Actual shared form and action components.</Help>
                    </div>
                    <div class="space-y-6 p-4">
                        <div>
                            <Label>Buttons</Label>
                            <div class="flex flex-wrap items-center gap-3">
                                <Button primary>Save</Button>
                                <Button primary outlined>Add Entry</Button>
                                <Button secondary outlined>Open</Button>
                                <Button danger>Delete</Button>
                                <Button secondary outlined size="xs">
                                    <Icon icon="trash-alt" />
                                </Button>
                            </div>
                        </div>

                        <div class="grid gap-4 md:grid-cols-2">
                            <div>
                                <Label>Text Input</Label>
                                <TextInput v-model="sampleText" />
                            </div>
                            <div>
                                <Label>Number Input</Label>
                                <NumberInput v-model="sampleNumber" :min="0" :step="15" />
                            </div>
                            <div>
                                <Label>Select Input</Label>
                                <SelectInput v-model="sampleSelect" :options="selectOptions" />
                            </div>
                            <div>
                                <Label>Switch</Label>
                                <div class="flex items-center gap-3 rounded border border-neutral-300 p-3 dark:border-neutral-700">
                                    <Switch v-model="sampleSwitch" />
                                    <div class="text-sm text-neutral-700 dark:text-neutral-300">Rest Timer</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="rounded border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black xl:col-span-5">
                    <div class="border-b border-neutral-200 p-4 dark:border-neutral-800">
                        <Subtitle class="!mb-1">Form Sections</Subtitle>
                        <Help class="!my-0">Settings-style layout using the actual section component.</Help>
                    </div>
                    <div class="px-4">
                        <FormSection title="Workout Features" description="Settings to show/hide features on the Workout form.">
                            <div class="space-y-4">
                                <div class="flex items-center justify-between gap-4">
                                    <div>
                                        <Label>Rest Timer</Label>
                                        <Help class="!my-0">Show configured rest timers in workouts and templates.</Help>
                                    </div>
                                    <Switch v-model="sampleSwitch" />
                                </div>
                                <div>
                                    <Label>Default Rest Timer</Label>
                                    <div class="flex items-center gap-3">
                                        <NumberInput v-model="sampleNumber" :min="0" :step="15" class="max-w-28" />
                                        <span class="text-sm text-neutral-500">seconds</span>
                                    </div>
                                </div>
                            </div>
                        </FormSection>
                    </div>
                </section>

                <section class="rounded border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black xl:col-span-7">
                    <div class="border-b border-neutral-200 p-4 dark:border-neutral-800">
                        <Subtitle class="!mb-1">Workout Rows</Subtitle>
                        <Help class="!my-0">Exercise and rest rows using production components.</Help>
                    </div>
                    <div class="p-4">
                        <div class="rounded bg-neutral-100 dark:bg-neutral-800">
                            <div class="bg-neutral-200 p-3 dark:bg-neutral-700">
                                <div class="font-semibold">Wed Jul 1, 2026</div>
                                <div class="text-sm text-neutral-600 dark:text-neutral-300">
                                    <Icon icon="stopwatch" />
                                    34 min
                                </div>
                            </div>
                            <div class="bg-neutral-400 p-1 text-center font-bold dark:bg-neutral-900">
                                X3 Bar Pull
                            </div>
                            <div class="p-3">
                                <ExerciseRow :exercise="sampleExercise" :last-exercise="sampleLastExercise" />
                                <RestTimerBlock
                                    :countdown-sound-enabled="false"
                                    :duration="90"
                                    label="Rest Timer"
                                    source="brand_board"
                                />
                                <ExerciseRow :exercise="sampleCurlExercise" />
                            </div>
                        </div>
                    </div>
                </section>

                <section class="rounded border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black xl:col-span-12">
                    <div class="border-b border-neutral-200 p-4 dark:border-neutral-800">
                        <Subtitle class="!mb-1">Calendar MVP</Subtitle>
                        <Help class="!my-0">An app-shell sketch that uses current component language without adding fake tabs or dashboard stats.</Help>
                    </div>
                    <div class="grid gap-6 p-4 lg:grid-cols-[390px_minmax(0,1fr)]">
                        <div class="rounded border border-neutral-300 bg-black p-6 dark:border-neutral-800">
                            <div class="overflow-hidden rounded bg-neutral-800">
                                <div class="flex items-center justify-between gap-3 bg-neutral-700 p-3">
                                    <Button primary outlined size="sm">
                                        <Icon fixed-width icon="chevron-left" />
                                    </Button>
                                    <div class="flex-1 text-center font-semibold">Jul 2026</div>
                                    <Button primary outlined size="sm">
                                        <Icon fixed-width icon="chevron-right" />
                                    </Button>
                                </div>
                                <div class="p-3">
                                    <div class="grid grid-cols-7 gap-1 pb-1 text-center text-xs font-semibold text-neutral-400">
                                        <div v-for="weekday in weekdays" :key="weekday">{{ weekday }}</div>
                                    </div>
                                    <div class="grid grid-cols-7 gap-1">
                                        <button
                                            v-for="day in calendarDays"
                                            :key="day.label"
                                            class="aspect-square rounded border p-1 text-left text-xs font-semibold"
                                            :class="calendarDayClass(day)"
                                        >
                                            {{ day.label }}
                                        </button>
                                    </div>
                                    <div class="mt-3 flex flex-wrap gap-x-3 gap-y-2 text-xs text-neutral-400">
                                        <div v-for="item in legend" :key="item.label" class="flex items-center gap-1.5">
                                            <span class="h-3.5 w-3.5 rounded border" :class="item.class"></span>
                                            {{ item.label }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 text-xs font-semibold uppercase text-neutral-400">Wed, Jul 1, 2026</div>
                        </div>

                        <div class="space-y-3">
                            <div class="rounded bg-neutral-100 dark:bg-neutral-800">
                                <div class="flex items-start justify-between gap-3 bg-neutral-200 p-3 dark:bg-neutral-700">
                                    <div>
                                        <div class="font-semibold">X3 Bar Pull</div>
                                        <div class="text-sm text-neutral-600 dark:text-neutral-300">34 min · 188 reps · 3 rest timers</div>
                                    </div>
                                    <Button primary outlined size="sm">Open</Button>
                                </div>
                                <div class="p-3">
                                    <ExerciseRow :exercise="sampleExercise" />
                                </div>
                            </div>
                            <div class="rounded bg-neutral-100 p-3 dark:bg-neutral-800">
                                <div class="mb-1 text-xs font-bold uppercase text-brand-primary-500">Log</div>
                                <div class="font-semibold">Body Weight</div>
                                <div class="text-sm text-neutral-600 dark:text-neutral-300">184.2 lb · Waist 33.5 in</div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import ExerciseRow from '@/components/Exercise/Row.vue';
import RestTimerBlock from '@/components/RestTimerBlock.vue';

export default defineComponent({
  name: 'BrandBoard',
  components: {
    ExerciseRow,
    RestTimerBlock,
  },
  data() {
    return {
      calendarDays: [
        {label: '28', level: 0, other: true},
        {label: '29', level: 0, other: true},
        {label: '30', level: 0, other: true},
        {label: '1', level: 1, selected: true},
        {label: '2', level: 2},
        {label: '3', level: 0},
        {label: '4', level: 1},
        {label: '5', level: 0},
        {label: '6', level: 1},
        {label: '7', level: 0},
        {label: '8', level: 2},
        {label: '9', level: 1},
        {label: '10', level: 0},
        {label: '11', level: 0},
      ],
      colors: [
        {name: 'brand 400', hex: '#4db9aa', textClass: 'text-black'},
        {name: 'brand 500', hex: '#309c8f', textClass: 'text-white'},
        {name: 'brand 900', hex: '#1d4440', textClass: 'text-white'},
        {name: 'brand 950', hex: '#0b2827', textClass: 'text-white'},
        {name: 'neutral 800', hex: '#262626', textClass: 'text-white'},
        {name: 'neutral 700', hex: '#404040', textClass: 'text-white'},
      ],
      legend: [
        {label: 'No entries', class: 'border-neutral-700 bg-neutral-900'},
        {label: '1 entry', class: 'border-brand-primary-400/30 bg-brand-primary-400/10'},
        {label: '2 entries', class: 'border-brand-primary-400/40 bg-brand-primary-400/20'},
        {label: '3+ entries', class: 'border-brand-primary-400/50 bg-brand-primary-400/30'},
      ],
      sampleNumber: 90,
      sampleSelect: 'workout',
      sampleSwitch: true,
      sampleText: 'Default Rest Timer',
      selectOptions: [
        {label: 'Workout', value: 'workout'},
        {label: 'Body Log', value: 'body-log'},
        {label: 'Progress Photo', value: 'progress-photo'},
      ],
      weekdays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    };
  },
  computed: {
    sampleExercise() {
      return {
        movement: {
          name: 'Row (Bent)',
          resistanceType: 'band',
          trackRepetitions: true,
          trackRepetitionsPartial: true,
          trackResistance: true,
        },
        bands: [
          {brandAndName: 'X3 Black'},
          {brandAndName: 'X3 White'},
        ],
        exhaustionLevel: 5,
        partialRepetitions: 3,
        repetitions: 25,
      };
    },
    sampleCurlExercise() {
      return {
        movement: {
          name: 'Biceps Curl',
          resistanceType: 'band',
          trackRepetitions: true,
          trackRepetitionsPartial: true,
          trackResistance: true,
        },
        bands: [
          {brandAndName: 'X3 White'},
        ],
        exhaustionLevel: 4,
        partialRepetitions: 2,
        repetitions: 18,
      };
    },
    sampleLastExercise() {
      return {
        partialRepetitions: 2,
        repetitions: 24,
      };
    },
  },
  methods: {
    calendarDayClass(day) {
      return [
        day.other ? 'opacity-40' : null,
        day.level === 0 ? 'border-neutral-700 bg-neutral-900' : null,
        day.level === 1 ? 'border-brand-primary-400/30 bg-brand-primary-400/10' : null,
        day.level === 2 ? 'border-brand-primary-400/40 bg-brand-primary-400/20' : null,
        day.level >= 3 ? 'border-brand-primary-400/50 bg-brand-primary-400/30' : null,
        day.selected ? '!border-brand-primary-400 ring-1 ring-brand-primary-400' : null,
      ];
    },
  },
});
</script>
